import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/app/components/ui/Button';
import { Field, Input } from '@/app/components/ui/Field';
import { cn } from '@/lib/cn';
import type { DashboardRange, RelativeRange } from '@/features/statistics/api';

const PRESETS: RelativeRange[] = ['last_7_days', 'last_30_days', 'this_week', 'this_month'];

function presetLabelKey(preset: RelativeRange) {
  switch (preset) {
    case 'last_7_days':
      return 'statistics.range.last7Days' as const;
    case 'last_30_days':
      return 'statistics.range.last30Days' as const;
    case 'this_week':
      return 'statistics.range.thisWeek' as const;
    case 'this_month':
      return 'statistics.range.thisMonth' as const;
  }
}

function isRelativeRange(value: string): value is RelativeRange {
  return (PRESETS as string[]).includes(value);
}

function parseRangeParam(value: string | null): DashboardRange {
  if (value && isRelativeRange(value)) return value;
  if (value?.includes('..')) {
    const [from, to] = value.split('..');
    if (from && to) return { from, to };
  }
  return 'last_30_days';
}

function serializeRange(range: DashboardRange): string {
  return typeof range === 'string' ? range : `${range.from}..${range.to}`;
}

/**
 * The period as a row of buttons above a grid ("last 7/30 days, this
 * week/month, custom"), kept in the URL (`?range=`) so a link carries the
 * period along. Uncontrolled by default — reads/writes `?range=` itself —
 * but also works controlled (`value`/`onChange`) for a caller that keeps its
 * own state (e.g. a preview that has not navigated yet).
 */
export function RangePicker({
  value,
  onChange,
  className,
}: {
  value?: DashboardRange;
  onChange?: (range: DashboardRange) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const current = value ?? parseRangeParam(searchParams.get('range'));
  const isCustom = typeof current === 'object';

  function set(range: DashboardRange) {
    if (onChange) {
      onChange(range);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('range', serializeRange(range));
    setSearchParams(next);
  }

  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)}>
      {PRESETS.map((preset) => (
        <Button
          key={preset}
          type="button"
          size="sm"
          variant={!isCustom && current === preset ? 'primary' : 'secondary'}
          onClick={() => set(preset)}
        >
          {t(presetLabelKey(preset))}
        </Button>
      ))}
      <Button
        type="button"
        size="sm"
        variant={isCustom ? 'primary' : 'secondary'}
        onClick={() => {
          if (isCustom) return;
          const to = new Date().toISOString().slice(0, 10);
          const from = new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10);
          set({ from, to });
        }}
      >
        {t('statistics.range.custom')}
      </Button>
      {isCustom && (
        <>
          <Field label={t('statistics.range.from')} className="mb-0">
            <Input
              type="date"
              value={current.from}
              onChange={(event) => set({ ...current, from: event.target.value })}
            />
          </Field>
          <Field label={t('statistics.range.to')} className="mb-0">
            <Input
              type="date"
              value={current.to}
              onChange={(event) => set({ ...current, to: event.target.value })}
            />
          </Field>
        </>
      )}
    </div>
  );
}
